import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { driverMethodCollectingRepository } from "../data/driverMethodCollectingRepository";
import { getDriverMethodCollectingUseCase } from "../domain/getDriverMethodCollectingUseCase";
import { AddDriverMethodCollectingModal } from "./AddDriverMethodCollectingModal";
import { EditDriverMethodCollectingModal } from "./EditDriverMethodCollectingModal";
import { canEditDriverMethodCollecting } from "../domain/updateDriverMethodCollectingUseCase";

export function DriverMethodCollectingModal({ driver, onClose }) {
  const [methods, setMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setMethods([]);
    setError(null);
    setOpenAddModal(false);
    setEditingMethod(null);

    async function loadMethods() {
      const result = await getDriverMethodCollectingUseCase(
        driverMethodCollectingRepository,
        driver.id,
      );
      if (!active) return;
      setMethods(result.data);
      setError(result.error);
      setIsLoading(false);
    }

    loadMethods();
    return () => {
      active = false;
    };
  }, [driver.id, attempt]);

  return (
    <Dialog
      open
      onClose={openAddModal || editingMethod ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="driver-method-collecting-title"
    >
      <DialogTitle id="driver-method-collecting-title">
        Métodos de cobro
        <Typography variant="subtitle1" component="span" display="block">
          {driver.fullname}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress
              color="secondary"
              aria-label="Cargando métodos de cobro"
            />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                onClick={() => setAttempt((value) => value + 1)}
              >
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : methods.length === 0 ? (
          <Typography py={2} textAlign="center">
            Este conductor no tiene métodos de cobro registrados.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Métodos de cobro del conductor">
              <TableHead>
                <TableRow>
                  <TableCell>Método</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Valor de cobro</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {methods.map((method) => (
                  <TableRow key={method.id}>
                    <TableCell>{method.name}</TableCell>
                    <TableCell>{method.code}</TableCell>
                    <TableCell
                      sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
                    >
                      {method.payment_value ?? "—"}
                    </TableCell>
                    <TableCell align="right">
                      {canEditDriverMethodCollecting(method) && (
                        <IconButton
                          color="secondary"
                          aria-label={`Editar ${method.name}`}
                          onClick={() => setEditingMethod(method)}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="secondary"
          disabled={isLoading || !!error}
          onClick={() => setOpenAddModal(true)}
        >
          Agregar
        </Button>
        <Button onClick={onClose} color="secondary">
          Cerrar
        </Button>
      </DialogActions>
      {openAddModal && (
        <AddDriverMethodCollectingModal
          key={driver.id}
          driver={driver}
          assignedMethods={methods}
          onClose={() => setOpenAddModal(false)}
          onSaved={(method) => {
            setMethods((current) => [...current, method].sort((a, b) => a.id - b.id));
            setOpenAddModal(false);
          }}
        />
      )}
      {editingMethod && (
        <EditDriverMethodCollectingModal
          key={`${driver.id}-${editingMethod.id}`}
          driver={driver}
          method={editingMethod}
          onClose={() => setEditingMethod(null)}
          onSaved={(updatedMethod) => {
            setMethods((current) => current.map((method) =>
              method.id === updatedMethod.id ? updatedMethod : method,
            ));
            setEditingMethod(null);
          }}
        />
      )}
    </Dialog>
  );
}
